import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Organization APIs
export const organizationAPI = {
  create: (data) => api.post('/organizations', data),
  get: (id) => api.get(`/organizations/${id}`),
  update: (id, data) => api.put(`/organizations/${id}`, data),
};

// Body APIs
export const bodyAPI = {
  create: (data) => api.post('/bodies', data),
  getAll: () => api.get('/bodies'),
  get: (id) => api.get(`/bodies/${id}`),
  update: (id, data) => api.put(`/bodies/${id}`, data),
  delete: (id) => api.delete(`/bodies/${id}`),
};

// Meeting APIs
export const meetingAPI = {
  create: (data) => api.post('/meetings', data),
  getAll: (params) => api.get('/meetings', { params }),
  get: (id) => api.get(`/meetings/${id}`),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
  getSchedule: () => api.get('/meeting-schedule'),
  updateSchedule: (data) => api.put('/meeting-schedule', data),
  generateSummary: (id) => api.post(`/meetings/${id}/generate-summary`),
  getSummary: (id) => api.get(`/meetings/${id}/summary`),
  downloadSummary: (id, format) => api.get(`/meetings/${id}/summary/download/${format}`, { responseType: 'blob' }),
  toggleSummaryShare: (id) => api.patch(`/meetings/${id}/summary/share`),
  getSharedSummaries: () => api.get('/portal/shared-summaries'),
};

// Agenda Item APIs
export const agendaAPI = {
  create: (data) => api.post('/agenda-items', data),
  getByMeeting: (meetingId) => api.get(`/meetings/${meetingId}/agenda-items`),
  update: (id, data) => api.put(`/agenda-items/${id}`, data),
  delete: (id) => api.delete(`/agenda-items/${id}`),
  reorder: (items) => api.post('/agenda-items/reorder', items),
};

// Attendance APIs
export const attendanceAPI = {
  create: (data) => api.post('/attendance', data),
  getByMeeting: (meetingId) => api.get(`/meetings/${meetingId}/attendance`),
  update: (id, data) => api.put(`/attendance/${id}`, data),
};

// Template APIs
export const templateAPI = {
  create: (data) => api.post('/templates', data),
  getAll: (params) => api.get('/templates', { params }),
  get: (id) => api.get(`/templates/${id}`),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  clone: (id) => api.post(`/templates/${id}/clone`),
  resolve: (params) => api.get('/templates/resolve', { params }),
  preview: (id, data) => api.post(`/templates/${id}/preview`, data || {}),
  generateFromTemplate: (data) => api.post('/templates/generate-from-template', data),
};

// Minutes APIs
export const minutesAPI = {
  create: (data) => api.post('/minutes', data),
  getAll: (params) => api.get('/minutes', { params }),
  get: (id) => api.get(`/minutes/${id}`),
  update: (id, data) => api.put(`/minutes/${id}`, data),
};

// Resolution APIs
export const resolutionAPI = {
  create: (data) => api.post('/resolutions', data),
  getAll: (params) => api.get('/resolutions', { params }),
  get: (id) => api.get(`/resolutions/${id}`),
  update: (id, data) => api.put(`/resolutions/${id}`, data),
  autoTag: (id) => api.post(`/resolutions/${id}/auto-tag`),
  getFilingSummary: () => api.get('/resolutions/filing-summary'),
};

// Action Item APIs
export const actionItemAPI = {
  create: (data) => api.post('/action-items', data),
  getAll: (params) => api.get('/action-items', { params }),
  getMyTasks: () => api.get('/action-items/my-tasks'),
  update: (id, data) => api.put(`/action-items/${id}`, data),
};

// Certificate APIs
export const certificateAPI = {
  create: (data) => api.post('/certificates', data),
  getAll: () => api.get('/certificates'),
  get: (id) => api.get(`/certificates/${id}`),
  update: (id, data) => api.put(`/certificates/${id}`, data),
};

// Record APIs
export const recordAPI = {
  create: (data) => api.post('/records', data),
  getAll: (params) => api.get('/records', { params }),
  getPublic: (orgId) => api.get(`/records/public?org_id=${orgId}`),
  get: (id) => api.get(`/records/${id}`),
  update: (id, data) => api.put(`/records/${id}`, data),
  delete: (id) => api.delete(`/records/${id}`),
};

// User APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  invite: (data) => api.post('/users/invite', data),
};

// Shareholder APIs
export const shareholderAPI = {
  create: (data) => api.post('/shareholders', data),
  getAll: () => api.get('/shareholders'),
  update: (id, data) => api.put(`/shareholders/${id}`, data),
  delete: (id) => api.delete(`/shareholders/${id}`),
};

// AI APIs
export const aiAPI = {
  draftMinutes: (meetingId) => api.post(`/ai/draft-minutes?meeting_id=${meetingId}`),
  draftResolution: (data) => api.post('/ai/draft-resolution', data),
  draftCertificate: (data) => api.post('/ai/draft-certificate', data),
  draftNotice: (data) => api.post('/ai/draft-notice', data),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentMeetings: () => api.get('/dashboard/recent-meetings'),
  getAnalytics: () => api.get('/dashboard/analytics'),
  getDeadlines: () => api.get('/dashboard/deadlines'),
};

// Notice APIs
export const noticeAPI = {
  send: (data) => api.post('/notices/send', data),
  getByMeeting: (meetingId) => api.get(`/meetings/${meetingId}/notices`),
};

// Proxy APIs
export const proxyAPI = {
  create: (data) => api.post('/proxies', data),
  getByMeeting: (meetingId) => api.get(`/meetings/${meetingId}/proxies`),
  revoke: (id) => api.put(`/proxies/${id}/revoke`),
};

// Shareholder Snapshot APIs
export const snapshotAPI = {
  create: (data) => api.post('/shareholder-snapshots', data),
  getByMeeting: (meetingId) => api.get(`/meetings/${meetingId}/snapshot`),
};

// Election APIs
export const electionAPI = {
  create: (data) => api.post('/elections', data),
  getByMeeting: (meetingId) => api.get(`/meetings/${meetingId}/elections`),
  update: (id, data) => api.put(`/elections/${id}`, data),
};

// Books & Logs APIs
export const booksAPI = {
  getMinutesBook: () => api.get('/books/minutes'),
  getResolutionsBook: () => api.get('/books/resolutions'),
  getNoticeLog: () => api.get('/books/notices'),
  getProxyLog: () => api.get('/books/proxies'),
};

// Audit Log APIs
export const auditLogAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
};

// Export APIs
export const exportAPI = {
  minutesPdf: (id) => `${BACKEND_URL}/api/export/minutes/${id}/pdf`,
  resolutionPdf: (id) => `${BACKEND_URL}/api/export/resolution/${id}/pdf`,
  certificatePdf: (id) => `${BACKEND_URL}/api/export/certificate/${id}/pdf`,
};

// Landlord APIs
export const landlordAPI = {
  getSettings: () => api.get('/landlord/settings'),
  updateSettings: (data) => api.put('/landlord/settings', data),
  getPackages: () => api.get('/landlord/packages'),
  getPackage: (id) => api.get(`/landlord/packages/${id}`),
  updatePackage: (id, data) => api.put(`/landlord/packages/${id}`, data),
  createPackage: (data) => api.post('/landlord/packages', data),
  getTenants: () => api.get('/landlord/tenants'),
  updateTenant: (id, data) => api.put(`/landlord/tenants/${id}`, data),
  suspendTenant: (id) => api.put(`/landlord/tenants/${id}/suspend`),
  activateTenant: (id) => api.put(`/landlord/tenants/${id}/activate`),
  getFinancialOverview: () => api.get('/landlord/financial-overview'),
  // Landlord Templates
  getTemplates: (params) => api.get('/landlord/templates', { params }),
  createTemplate: (data) => api.post('/landlord/templates', data),
  updateTemplate: (id, data) => api.put(`/landlord/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/landlord/templates/${id}`),
  uploadTemplateFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/landlord/templates/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  previewTemplate: (id, data) => api.post(`/landlord/templates/${id}/preview`, data || {}),
  // Orders
  getOrders: (params) => api.get('/landlord/orders', { params }),
  approveOrder: (id, data) => api.put(`/landlord/orders/${id}/approve`, data || {}),
  rejectOrder: (id, data) => api.put(`/landlord/orders/${id}/reject`, data || {}),
  deleteProof: (id) => api.delete(`/landlord/orders/${id}/proof`),
  // Tenant Management
  getTenantDetails: (id) => api.get(`/landlord/tenants/${id}/details`),
  impersonateTenant: (id) => api.post(`/landlord/tenants/${id}/impersonate`),
  makeUserAdmin: (userId) => api.post(`/landlord/users/${userId}/make-admin`),
  sendResetLink: (userId) => api.post(`/landlord/users/${userId}/send-reset-link`),
  extendTrial: (id, data) => api.post(`/landlord/tenants/${id}/extend-trial`, data || {}),
  grantFreeSubscription: (id) => api.post(`/landlord/tenants/${id}/grant-free`),
  deleteTenant: (id) => api.delete(`/landlord/tenants/${id}`),
  // Tenant Backup & Restore
  exportTenantsBackup: (saveToDb = true) => api.post(`/landlord/tenants/backup/export?save_to_db=${saveToDb}`),
  listTenantsBackups: () => api.get('/landlord/tenants/backup/list'),
  restoreTenantsBackup: (formData) => api.post('/landlord/tenants/backup/restore', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteTenantsBackup: (id) => api.delete(`/landlord/tenants/backup/${id}`),
};

// Email APIs
export const emailAPI = {
  getConfig: () => api.get('/email/config'),
  updateConfig: (data) => api.put('/email/config', data),
  testEmail: (data) => api.post('/email/test', data),
  sendEmail: (data) => api.post('/email/send', data),
  getLogs: () => api.get('/email/logs'),
  sendNotice: (data) => api.post('/email/send-notice', data),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Signature APIs
export const signatureAPI = {
  getMy: () => api.get('/signatures/my'),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/signatures/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  saveDrawn: (data) => api.post('/signatures/save-drawn', data),
  deleteMy: () => api.delete('/signatures/my'),
  getUser: (userId) => api.get(`/signatures/user/${userId}`),
};

// Board Composition APIs
export const boardCompositionAPI = {
  getAll: () => api.get('/board-composition'),
  getCurrent: () => api.get('/board-composition/current'),
  create: (data) => api.post('/board-composition', data),
  update: (id, data) => api.put(`/board-composition/${id}`, data),
  delete: (id) => api.delete(`/board-composition/${id}`),
};

// Archive APIs
export const archiveAPI = {
  getSettings: () => api.get('/archive/settings'),
  updateSettings: (data) => api.put('/archive/settings', data),
  execute: (data) => api.post('/archive/execute', data),
  getLogs: () => api.get('/archive/logs'),
  getDueRecords: () => api.get('/archive/due'),
};

// Password Reset APIs (no auth required)
export const passwordResetAPI = {
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyCode: (email, code) => api.post('/auth/verify-reset-code', { email, code }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
};

// Document Version History APIs
export const versionAPI = {
  getVersions: (entityType, entityId) => api.get(`/versions/${entityType}/${entityId}`),
  createVersion: (data) => api.post('/versions', data),
  getDetail: (versionId) => api.get(`/versions/detail/${versionId}`),
  revert: (versionId) => api.post(`/versions/revert/${versionId}`),
  delete: (versionId) => api.delete(`/versions/${versionId}`),
};

// Governance Portal APIs
export const governanceAPI = {
  getDocuments: () => api.get('/governance/documents'),
  getDocument: (id) => api.get(`/governance/documents/${id}`),
  shareDocument: (data) => api.post('/governance/documents', data),
  documentAction: (id, data) => api.post(`/governance/documents/${id}/action`, data),
  deleteDocument: (id) => api.delete(`/governance/documents/${id}`),
  extendDeadline: (id, data) => api.put(`/governance/documents/${id}/deadline`, data),
  requestExtension: (id, data) => api.post(`/governance/documents/${id}/request-extension`, data),
  addComment: (id, data) => api.post(`/governance/documents/${id}/comment`, data),
  uploadFile: (formData) => api.post('/governance/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getFile: (id) => api.get(`/governance/files/${id}`, { responseType: 'blob' }),
  sendGroupEmail: (data) => api.post('/governance/send-group-email', data),
  getUserGroups: () => api.get('/governance/user-groups'),
  // Polls
  getPolls: () => api.get('/governance/polls'),
  createPoll: (data) => api.post('/governance/polls', data),
  castVote: (pollId, data) => api.post(`/governance/polls/${pollId}/vote`, data),
  closePoll: (pollId) => api.put(`/governance/polls/${pollId}/close`),
  deletePoll: (pollId) => api.delete(`/governance/polls/${pollId}`),
  // Stakeholder Requests
  getRequests: () => api.get('/governance/requests'),
  createRequest: (data) => api.post('/governance/requests', data),
  updateRequestStatus: (id, data) => api.put(`/governance/requests/${id}/status`, data),
  // Stakeholders Portal
  getPortalSharedItems: () => api.get('/governance/portal/shared'),
};

// Subscription APIs (tenant-facing)
export const subscriptionAPI = {
  getPackages: () => api.get('/subscription/packages'),
  getStatus: () => api.get('/subscription/status'),
  upgrade: (data) => api.post('/subscription/upgrade', data),
  getPaymentMethods: () => api.get('/subscription/payment-methods'),
  createOrder: (data) => api.post('/subscription/orders', data),
  getOrders: () => api.get('/subscription/orders'),
  getOrder: (id) => api.get(`/subscription/orders/${id}`),
  uploadProof: (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/subscription/orders/${orderId}/proof`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ─── Corporate Documents ───
export const corporateAPI = {
  // Announcements
  getAnnouncements: () => api.get('/corporate/announcements'),
  getAllAnnouncements: () => api.get('/corporate/announcements/all'),
  upsertAnnouncement: (formData) => api.post('/corporate/announcements', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggleAnnouncementPublish: (id) => api.patch(`/corporate/announcements/${id}/publish`),
  deleteAnnouncement: (id) => api.delete(`/corporate/announcements/${id}`),
  // Credentials
  getCredentials: () => api.get('/corporate/credentials'),
  upsertCredential: (formData) => api.post('/corporate/credentials', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCredential: (id, data) => api.patch(`/corporate/credentials/${id}`, data),
  deleteCredential: (id) => api.delete(`/corporate/credentials/${id}`),
  toggleCredentialPublish: (id) => api.patch(`/corporate/credentials/${id}/publish`),
  // Requirements
  getRequirements: () => api.get('/corporate/requirements'),
  upsertRequirement: (formData) => api.post('/corporate/requirements', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggleRequirementStatus: (id) => api.patch(`/corporate/requirements/${id}/status`),
  toggleRequirementPublish: (id) => api.patch(`/corporate/requirements/${id}/publish`),
  deleteRequirement: (id) => api.delete(`/corporate/requirements/${id}`),
  seedDefaults: () => api.post('/corporate/requirements/seed-defaults'),
  getDefaults: () => api.get('/corporate/requirements/defaults'),
  adoptDefaults: () => api.post('/corporate/requirements/adopt-defaults'),
  // Resources
  getResources: () => api.get('/corporate/resources'),
  upsertResource: (formData) => api.post('/corporate/resources', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateResource: (id, data) => api.patch(`/corporate/resources/${id}`, data),
  toggleResourcePublish: (id) => api.patch(`/corporate/resources/${id}/publish`),
  deleteResource: (id) => api.delete(`/corporate/resources/${id}`),
  // Communications
  getCommunications: () => api.get('/corporate/communications'),
  createCommunication: (formData) => api.post('/corporate/communications', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCommunication: (id) => api.delete(`/corporate/communications/${id}`),
};

export const libraryAPI = {
  upload: (formData) => api.post('/library/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};


export default api;
